import { Button } from "@material-ui/core";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import MembersErrorDialog from "@saleor/permissionGroups/components/MembersErrorDialog";
import MfaToggleSwitchDialog from "@saleor/permissionGroups/components/MfaToggleSwitchDialog";
import {
  arePermissionsExceeded,
  permissionsDiff,
  usersDiff
} from "@saleor/permissionGroups/utils/utils";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import PermissionGroupDetailsPage, {
  PermissionGroupDetailsPageFormData
} from "../../components/PermissionGroupDetailsPage";
import UnassignMembersDialog from "../../components/UnassignMembersDialog";
import { usePermissionGroupUpdate } from "../../mutations";
import { usePermissionGroupDetailsQuery } from "../../queries";
import { PermissionGroupUpdate } from "../../types/PermissionGroupUpdate";
import {
  permissionGroupDetailsUrl,
  PermissionGroupDetailsUrlDialog,
  PermissionGroupDetailsUrlQueryParams,
  permissionGroupListUrl
} from "../../urls";

interface PermissionGroupDetailsProps {
  id: string;
  params: PermissionGroupDetailsUrlQueryParams;
}

export const PermissionGroupDetails: React.FC<PermissionGroupDetailsProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const shop = useShop();
  const notify = useNotifier();
  const intl = useIntl();
  const user = useUser();
  const [isMfaEnabled, setIs2FAEnabled] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const { data, loading, refetch } = usePermissionGroupDetailsQuery({
    displayLoader: true,
    variables: { id, userId: user?.user.id }
  });

  const [membersList, setMembersList] = useStateFromProps(
    data?.permissionGroup.users
  );

  const [membersModified, setMembersModified] = useState(false);

  const handleUpdateSuccess = (data: PermissionGroupUpdate) => {
    if (data.permissionGroupUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });

      if (isMfaEnabled) {
        notify({
          status: "success",
          text: "Two-Factor Authentication (2FA) Enabled Successfully!"
        });
      } else {
        notify({
          status: "success",
          text: "Two-Factor Authentication (2FA) Disabled Successfully!"
        });
      }

      setHasChanges(false);

      try {
        localStorage.setItem(
          "coupon_usage_limit_permission_updated",
          Date.now().toString()
        );
      } catch (e) {
        // Silently ignore storage error
      }

      refetch();
      closeModal();
    } else if (
      data.permissionGroupUpdate.errors.some(e => e.field === "removeUsers")
    ) {
      openModal("unassignError");
    }
  };

  const { isSelected, listElements, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const [
    permissionGroupUpdate,
    permissionGroupUpdateResult
  ] = usePermissionGroupUpdate({
    onCompleted: handleUpdateSuccess
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    PermissionGroupDetailsUrlDialog,
    PermissionGroupDetailsUrlQueryParams
  >(navigate, params => permissionGroupDetailsUrl(id, params), params);

  const handleSort = createSortHandler(
    navigate,
    params => permissionGroupDetailsUrl(id, params),
    params
  );

  const unassignMembers = () => {
    setMembersList(membersList?.filter(m => !listElements.includes(m.id)));
    setMembersModified(true);
    closeModal();
  };

  const isGroupEditable =
    (data?.user.editableGroups || []).filter(g => g.id === id).length > 0;

  const lastSourcesOfPermission = (data?.user.userPermissions || [])
    .filter(
      perm =>
        perm.sourcePermissionGroups.length === 1 &&
        perm.sourcePermissionGroups[0].id === id
    )
    .map(perm => perm.code);

  const userPermissions = user?.user.userPermissions.map(p => p.code) || [];

  const permissions = (shop?.permissions || []).map(perm => ({
    ...perm,
    disabled: !userPermissions.includes(perm.code),
    lastSource: lastSourcesOfPermission.includes(perm.code)
  }));

  const permissionsExceeded = arePermissionsExceeded(
    data?.permissionGroup,
    user.user
  );
  const disabled = loading || !isGroupEditable || permissionsExceeded;
  const handleSubmit = async (formData: PermissionGroupDetailsPageFormData) => {
    const newMfaEnabled = isMfaEnabled;
    const mfaEnabledDate = newMfaEnabled
      ? new Date().toISOString().slice(0, -1)
      : null;
    setHasChanges(newMfaEnabled !== (mfaEnabledDate !== null));
    const result = await permissionGroupUpdate({
      variables: {
        id,
        input: {
          name: formData.name,
          ...permissionsDiff(data?.permissionGroup, formData),
          ...usersDiff(data?.permissionGroup, formData),
          mfaEnabledDate
        }
      }
    });

    return result.data.permissionGroupUpdate.errors;
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleConfirm = () => {
    const new2FAEnabledState = !isMfaEnabled;
    setIs2FAEnabled(new2FAEnabledState);
    setHasChanges(
      new2FAEnabledState !== (data?.permissionGroup.mfaEnabledDate !== null)
    );
    setOpen(false);
  };

  React.useEffect(() => {
    setIs2FAEnabled(data?.permissionGroup.mfaEnabledDate);
  }, [data?.permissionGroup.mfaEnabledDate]);

  return (
    <>
      <PermissionGroupDetailsPage
        permissionGroup={data?.permissionGroup}
        permissionsExceeded={permissionsExceeded}
        members={membersList || []}
        membersModified={membersModified}
        onBack={() => navigate(permissionGroupListUrl())}
        onAssign={() => openModal("assign")}
        onUnassign={ids => openModal("unassign", { ids })}
        errors={
          permissionGroupUpdateResult?.data?.permissionGroupUpdate.errors || []
        }
        onSubmit={handleSubmit}
        permissions={permissions}
        saveButtonBarState={permissionGroupUpdateResult.status}
        disabled={disabled}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        sort={getSortParams(params)}
        toolbar={
          <Button
            color="primary"
            onClick={() => openModal("unassign", { ids: listElements })}
          >
            {intl.formatMessage({
              defaultMessage: "Unassign",
              description: "button title"
            })}
          </Button>
        }
        onSort={handleSort}
        isMfaEnabled={isMfaEnabled}
        setIs2FAEnabled={setIs2FAEnabled}
        hasChangesMfa={hasChanges}
        handleSwitchChange={handleOpen}
      />
      <MfaToggleSwitchDialog
        open={open}
        onConfirm={handleConfirm}
        onClose={handleClose}
        isMfaEnabled={isMfaEnabled}
      />
      <UnassignMembersDialog
        onConfirm={unassignMembers}
        confirmButtonState={permissionGroupUpdateResult.status}
        quantity={listElements.length}
        open={params.action === "unassign"}
        onClose={closeModal}
      />
      <MembersErrorDialog
        onConfirm={closeModal}
        confirmButtonState={permissionGroupUpdateResult.status}
        open={params.action === "unassignError"}
        onClose={closeModal}
      />
    </>
  );
};

export default PermissionGroupDetails;
